import type { InputHTMLAttributes, ReactNode, Ref } from 'react';
import { ErrorMessage } from './ErrorMessage';
import { Hint } from './Hint';
import { Label } from './Label';
import { RequiredMarker } from './RequiredMarker';
import { cx, describedBy, widthClass, type FluidWidth, type InputWidth } from './cx';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'id' | 'width'> {
  id: string;
  label: ReactNode;
  labelClassName?: string;
  labelLang?: string;
  isPageHeading?: boolean;
  hint?: ReactNode;
  hintLang?: string;
  error?: string;
  /** a red asterisk after the label / legend ("(povinné)" for screen readers) */
  required?: boolean;
  width?: InputWidth | FluidWidth;
  formGroupClassName?: string;
  /** Extra ids for aria-describedby (e.g. the hint of a disabled field). */
  extraDescribedBy?: string;
  /** Content rendered between the error message and the input (e.g. a "disabled" explanation). */
  beforeInput?: ReactNode;
  prefix?: string;
  suffix?: string;
  ref?: Ref<HTMLInputElement>;
}

/** govuk/components/input */
export function Input({
  id,
  label,
  labelClassName,
  labelLang,
  isPageHeading,
  hint,
  hintLang,
  error,
  required,
  width,
  formGroupClassName,
  extraDescribedBy,
  beforeInput,
  className,
  type = 'text',
  ref,
  ...rest
}: InputProps) {
  const hintId = hint ? `${id}-hint` : undefined;
  const errorId = error ? `${id}-error` : undefined;
  return (
    <div className={cx('govuk-form-group', error && 'govuk-form-group--error', formGroupClassName)}>
      <Label htmlFor={id} className={labelClassName} isPageHeading={isPageHeading} lang={labelLang}>
        {label}
        <RequiredMarker required={required} />
      </Label>
      {hint && (
        <Hint id={hintId} lang={hintLang}>
          {hint}
        </Hint>
      )}
      {error && <ErrorMessage id={errorId}>{error}</ErrorMessage>}
      {beforeInput}
      <input
        ref={ref}
        className={cx('govuk-input', widthClass(width), className, error && 'govuk-input--error')}
        id={id}
        type={type}
        aria-describedby={describedBy(extraDescribedBy, hintId, errorId)}
        aria-invalid={error ? true : undefined}
        {...rest}
      />
    </div>
  );
}
